import {
  type Chain,
  type DynamicClient,
  type WalletProviderMetadata,
  assertWalletAccountSigningAvailability,
} from '@dynamic-labs-sdk/client';
import {
  DYNAMIC_ICONIC_SPRITE_URL,
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Psbt } from 'bitcoinjs-lib';

import type {
  BitcoinSendTransactionResponse,
  BitcoinWalletProvider,
  SignPsbtOptions,
} from '../../../../BitcoinWalletProvider.types';
import { bitcoinExecuteCheckoutTransaction } from '../../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../../utils/bitcoinTransferAmount';
import { createPsbtOptions } from '../../../../utils/createPsbtOptions';
import { getNetworkIdFromNetworkName } from '../../../../utils/getNetworkIdFromNetworkName';
import type { UnisatInjectedProvider } from '../UnisatInjectedProvider.types';

export const UNISAT_METADATA: WalletProviderMetadata = {
  displayName: 'UniSat',
  icon: `${DYNAMIC_ICONIC_SPRITE_URL}#unisat`,
};

type CreateUnisatWalletProviderParams = {
  dynamicClient: DynamicClient;
  injectedProvider: UnisatInjectedProvider;
};

export const createUnisatWalletProvider = ({
  dynamicClient,
  injectedProvider,
}: CreateUnisatWalletProviderParams): BitcoinWalletProvider => {
  const chain: Chain = 'BTC';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: UNISAT_METADATA.displayName,
    walletProviderType,
  });

  const connect: BitcoinWalletProvider['connect'] = async () => {
    const addresses = await injectedProvider.requestAccounts();

    return {
      addresses: addresses.map((address) => ({
        address,
      })),
    };
  };

  const disconnect: BitcoinWalletProvider['disconnect'] = async () => {
    return injectedProvider.disconnect();
  };

  const getActiveNetworkId: BitcoinWalletProvider['getActiveNetworkId'] =
    async () => {
      const chain = await injectedProvider.getChain();

      let networkName = 'mainnet';
      if (chain.enum === 'BITCOIN_TESTNET') {
        networkName = 'testnet';
      } else if (chain.enum === 'BITCOIN_SIGNET') {
        networkName = 'signet';
      }

      return getNetworkIdFromNetworkName({ networkName }, dynamicClient);
    };

  const getConnectedAddresses: BitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const accounts = await injectedProvider.getAccounts();

      return {
        addresses: accounts,
      };
    };

  const sendBitcoin: BitcoinWalletProvider['sendBitcoin'] = async ({
    transaction,
    walletAccount,
  }): Promise<BitcoinSendTransactionResponse> => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const transactionId = await injectedProvider.sendBitcoin(
      transaction.recipientAddress,
      Number(transaction.amount)
    );

    return {
      transactionId,
    };
  };

  const signMessage: BitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
    protocol,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signedMessage = await injectedProvider.signMessage(message, protocol);

    return {
      signature: signedMessage,
    };
  };

  const signPsbt: BitcoinWalletProvider['signPsbt'] = async ({
    request,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64);

    const signedPsbtHex = await injectedProvider.signPsbt(
      psbtFromBase64.toHex(),
      createPsbtOptions({ psbt: psbtFromBase64, request })
    );

    return { signedPsbt: Psbt.fromHex(signedPsbtHex).toBase64() };
  };

  const signPsbts: BitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const psbtHexs: string[] = [];
    const psbtOptions: SignPsbtOptions[] = [];

    for (const request of requests) {
      const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64);

      psbtHexs.push(psbtFromBase64.toHex());
      psbtOptions.push(createPsbtOptions({ psbt: psbtFromBase64, request }));
    }

    const signedPsbtHexs = await injectedProvider.signPsbts(
      psbtHexs,
      psbtOptions
    );

    return {
      signedPsbts: signedPsbtHexs.map((signedPsbtHex) =>
        Psbt.fromHex(signedPsbtHex).toBase64()
      ),
    };
  };

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter({
      removeEventListeners: () => {
        injectedProvider?.removeAllListeners();
      },

      setupEventListeners: ({
        handleAccountsChanged,
        handleNetworkChanged,
      }) => {
        injectedProvider?.on('accountsChanged', (addresses) => {
          handleAccountsChanged({ addresses });
        });

        injectedProvider?.on('networkChanged', (network) => {
          handleNetworkChanged({ networkId: network });
        });
      },

      supportedEvents: ['accountsChanged', 'networkChanged'],
    });

  const terminate: BitcoinWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  return {
    chain,
    connect,
    disconnect,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(UNISAT_METADATA.displayName),
    key,
    metadata: UNISAT_METADATA,
    sendBitcoin,
    signMessage,
    signPsbt,
    signPsbts,
    terminate,
    transferAmount: (args) => bitcoinTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
